/**
 * PolicyRuleDialog — create / edit a single deterministic policy rule.
 *
 * Surfaces the field set the V1 policy engine actually evaluates today
 * (M-WG §4.4 + M-SPEC-V1 §6 — see `packages/shared-types/src/entities/
 * policy-rule.ts`). Advanced fields that the engine accepts but rarely
 * need editing through the UI (subject_wg_all_of / none_of, ABAC
 * conditions, data_class / side_effect caps) live behind an
 * "Advanced" toggle so the default form stays scannable.
 *
 * The proxy's `PolicyRulesetLoader` polls the registry DB every 30s
 * (`POLICY_REFRESH_INTERVAL_MS`), so a save here is live without a
 * proxy restart. Start with `status: 'draft'` while authoring; flip
 * to `active` when you want the rule to take effect.
 */
import { useEffect, useState, type JSX } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import {
  useCreatePolicy,
  useDeletePolicy,
  useUpdatePolicy,
  type CreatePolicyInput,
  type PolicyRuleRow,
} from '@/api/index.js';

const POLICY_ACTIONS = [
  'discover',
  'invoke',
  'register',
  'approve',
  'admin',
  'audit_read',
] as const;
const POLICY_EFFECTS = ['allow', 'deny'] as const;
const POLICY_STATUSES = ['draft', 'active', 'deprecated'] as const;

const REASON_CODE_RE = /^[A-Z][A-Z0-9_]*$/;

interface FormState {
  priority: string;
  name: string;
  action: (typeof POLICY_ACTIONS)[number];
  effect: (typeof POLICY_EFFECTS)[number];
  reason_code: string;
  status: (typeof POLICY_STATUSES)[number];
  version: string;
  subject_wg_any_of: string;
  resource_tool_name: string;
  resource_server_slug: string;
}

function defaultForm(): FormState {
  return {
    priority: '100',
    name: '',
    action: 'invoke',
    effect: 'deny',
    reason_code: '',
    status: 'draft',
    version: '1',
    subject_wg_any_of: '',
    resource_tool_name: '',
    resource_server_slug: '',
  };
}

function rowToForm(row: PolicyRuleRow): FormState {
  const rs = (row.resourceSelector ?? {}) as Record<string, unknown>;
  return {
    priority: String(row.priority),
    name: row.name,
    action: (POLICY_ACTIONS.includes(row.action as never)
      ? row.action
      : 'invoke') as FormState['action'],
    effect: (row.effect === 'allow' ? 'allow' : 'deny') as FormState['effect'],
    reason_code: row.reasonCode,
    status: (POLICY_STATUSES.includes(row.status as never)
      ? row.status
      : 'draft') as FormState['status'],
    version: row.version,
    subject_wg_any_of: (row.subjectWgAnyOf ?? []).join(', '),
    resource_tool_name: typeof rs.tool_name === 'string' ? rs.tool_name : '',
    resource_server_slug: typeof rs.server_slug === 'string' ? rs.server_slug : '',
  };
}

function parseList(raw: string): string[] | undefined {
  const items = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return items.length > 0 ? items : undefined;
}

function formToPayload(form: FormState): CreatePolicyInput | { error: string } {
  const priority = Number.parseInt(form.priority, 10);
  if (!Number.isFinite(priority) || priority < 0 || priority > 99999) {
    return { error: 'priority must be an integer in [0, 99999]' };
  }
  if (!form.name.trim()) return { error: 'name is required' };
  if (!form.reason_code.trim()) return { error: 'reason_code is required' };
  if (!REASON_CODE_RE.test(form.reason_code)) {
    return { error: 'reason_code must be UPPER_SNAKE_CASE (e.g., BLOCK_HIGH_RISK)' };
  }

  const resourceSelector: Record<string, string> = {};
  if (form.resource_tool_name.trim()) resourceSelector.tool_name = form.resource_tool_name.trim();
  if (form.resource_server_slug.trim()) {
    resourceSelector.server_slug = form.resource_server_slug.trim();
  }

  return {
    priority,
    name: form.name.trim(),
    action: form.action,
    effect: form.effect,
    reason_code: form.reason_code.trim(),
    status: form.status,
    version: form.version.trim() || '1',
    subject_wg_any_of: parseList(form.subject_wg_any_of),
    resource_selector: Object.keys(resourceSelector).length > 0 ? resourceSelector : undefined,
  };
}

export interface PolicyRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass a row to edit; omit to create. */
  rule?: PolicyRuleRow | null;
}

export function PolicyRuleDialog({ open, onOpenChange, rule }: PolicyRuleDialogProps): JSX.Element {
  const create = useCreatePolicy();
  const update = useUpdatePolicy();
  const remove = useDeletePolicy();
  const isEdit = Boolean(rule);
  const [form, setForm] = useState<FormState>(() => (rule ? rowToForm(rule) : defaultForm()));
  const [error, setError] = useState<string | null>(null);

  // Reset form when the dialog is reopened with a different rule (or for create).
  useEffect(() => {
    if (open) {
      setForm(rule ? rowToForm(rule) : defaultForm());
      setError(null);
    }
  }, [open, rule]);

  const busy = create.isPending || update.isPending || remove.isPending;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const payload = formToPayload(form);
    if ('error' in payload) {
      setError(payload.error);
      return;
    }
    try {
      if (isEdit && rule) {
        await update.mutateAsync({ id: rule.id, patch: payload });
      } else {
        await create.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'request failed');
    }
  }

  async function handleDelete(): Promise<void> {
    if (!rule) return;
    if (
      !window.confirm(
        `Delete policy rule "${rule.name}"? This removes it immediately; the proxy reloads within 30s.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await remove.mutateAsync(rule.id);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete failed');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit policy rule' : 'New policy rule'}</DialogTitle>
          <DialogDescription>
            Deterministic rule. Evaluated in priority order (lower number wins); first match stops
            evaluation. Save as <code>draft</code> first; flip to <code>active</code> when ready.
            The proxy reloads within {`{POLICY_REFRESH_INTERVAL_MS}`} (default 30s).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="policy-priority">Priority</Label>
              <Input
                id="policy-priority"
                inputMode="numeric"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                placeholder="100"
              />
              <p className="text-xs text-muted-foreground">0–99999, lower wins.</p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="policy-name">Name</Label>
              <Input
                id="policy-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="block-destructive-after-hours"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="policy-action">Action</Label>
              <Select
                value={form.action}
                onValueChange={(v) => setForm({ ...form, action: v as FormState['action'] })}
              >
                <SelectTrigger id="policy-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POLICY_ACTIONS.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="policy-effect">Effect</Label>
              <Select
                value={form.effect}
                onValueChange={(v) => setForm({ ...form, effect: v as FormState['effect'] })}
              >
                <SelectTrigger id="policy-effect">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POLICY_EFFECTS.map((e) => (
                    <SelectItem key={e} value={e}>
                      {e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="policy-status">Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v as FormState['status'] })}
              >
                <SelectTrigger id="policy-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POLICY_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="policy-reason">Reason code</Label>
              <Input
                id="policy-reason"
                value={form.reason_code}
                onChange={(e) => setForm({ ...form, reason_code: e.target.value.toUpperCase() })}
                placeholder="BLOCK_HIGH_RISK"
              />
              <p className="text-xs text-muted-foreground">
                UPPER_SNAKE_CASE — surfaced in audit + denial responses.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="policy-version">Version</Label>
              <Input
                id="policy-version"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="1"
              />
              <p className="text-xs text-muted-foreground">
                Bump on edits — surfaced in the rules table.
              </p>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="policy-wg-any">Subject — workgroups (any-of)</Label>
            <Input
              id="policy-wg-any"
              value={form.subject_wg_any_of}
              onChange={(e) => setForm({ ...form, subject_wg_any_of: e.target.value })}
              placeholder="mcp:fingate, mcp:admin"
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated WG IDs. Leave blank to apply to every principal.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="policy-tool">Resource — tool name (optional)</Label>
              <Input
                id="policy-tool"
                value={form.resource_tool_name}
                onChange={(e) => setForm({ ...form, resource_tool_name: e.target.value })}
                placeholder="finance.execute_trade"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="policy-server">Resource — server slug (optional)</Label>
              <Input
                id="policy-server"
                value={form.resource_server_slug}
                onChange={(e) => setForm({ ...form, resource_server_slug: e.target.value })}
                placeholder="fingate-prod"
              />
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <DialogFooter className="flex flex-row items-center justify-between gap-2 sm:justify-between">
            <div>
              {isEdit && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={busy}>
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create rule'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
